import NextImage from '@allesauto/core/nextImage';
import Price from '@allesauto/core/price';
import translate from '@allesauto/i18n';
import { ViewModes } from '@allesauto/utils/consts';
import type { AccessoriesProduct } from '@allesauto/utils/types.d';
import { ArrowRight20 } from '@carbon/icons-react';
import clsx from 'clsx';
import React, { FC, useCallback } from 'react';
import { useIntl } from 'react-intl';

export interface AccessoriesTopRatedItemProps {
  product: AccessoriesProduct;
  allowClick?: boolean;
  onClick?: (productId: string) => void;
}

const AccessoriesTopRatedItem: FC<
  React.PropsWithChildren<AccessoriesTopRatedItemProps>
> = ({ product, allowClick = true, onClick }) => {
  const intl = useIntl();

  const handleClickEvent = useCallback(() => {
    if (allowClick && onClick) {
      onClick(product.productId);
    }
  }, [allowClick, onClick, product]);

  const className = {
    container: clsx(
      'bg-transparent cursor-pointer flex flex-col group p-15px relative w-full hover:bg-white',
      "after:absolute after:bg-green60 after:bottom-0 after:content-[''] after:duration-[400ms] after:ease-linear after:h-0 after:left-0 after:overflow-hidden after:transition-[height] after:w-full hover:after:h-7px",
      'wl-bg-highlight-after monochrome:after:bg-black'
    ),
    title:
      'break-words h-full mb-15px font-regular line-clamp overflow-hidden text-ellipsis text-p1 monochrome:text-black',
    imageContainer: 'h-full mb-15px bg-white relative',
    image: 'block pt-[62.5%] relative pointer-events-none',
    imageShadow:
      'shadow-[inset_0_0_3.6rem_rgba(0,0,0,0.3)] absolute w-full h-full top-0 left-0',
    bottomRow:
      'flex items-center justify-between text-purple80 monochrome:text-black',
    priceWrapper: 'block group-hover:hidden',
    price: '!text-headline4',
    priceHover: 'hidden md:py-[2px] group-hover:block',
    arrowRight: 'opacity-0 group-hover:opacity-100',
  };

  return (
    <div onClick={handleClickEvent} className={className.container}>
      <h4 className={className.title}>{product.title}</h4>
      <div className={className.imageContainer}>
        <div className={className.image}>
          <NextImage
            baseUrls=""
            unoptimized
            alt={product.title}
            src={product.galleryThumbnail}
            layout="fill"
            objectFit="contain"
            priority={true}
          />
        </div>
        <div className={className.imageShadow}></div>
      </div>
      <div className={className.bottomRow}>
        <Price
          priceTextKey="ui.PRICE.PRICE_ONLY"
          price={product.price}
          displayView={ViewModes.GRID}
          classNames={{
            wrapperClass: className.priceWrapper,
            mainPriceClass: className.price,
          }}
          hasFractionDigits={true}
        />
        <div className={className.priceHover}>
          {translate(intl, 'ui.SHOPPING_CART.ACCESSORIES.ITEM_LINK')}
        </div>
        <ArrowRight20 className={className.arrowRight} />
      </div>
    </div>
  );
};

export default AccessoriesTopRatedItem;
